import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package2, ArrowRight, ChevronRight, LogOut } from "lucide-react";
import api from "../api";
import { useAuth } from "../contexts/AuthContext";
import useNoIndex from "../hooks/useNoIndex";

export default function Orders() {
  const [orders, setOrders] = useState(null);
  const [err, setErr] = useState("");
  const { user, logout } = useAuth();
  useNoIndex();
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/orders")
      .then(({ data }) => setOrders(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Unable to load orders"));
  }, []);

  const handleSignOut = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-16">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="overline">YOUR ACCOUNT</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light mt-3">Order History</h1>
          <p className="text-sm text-[#8A94A6] mt-3">
            Review your past research compound orders.
            {user?.email && (
              <span className="block mt-2 font-mono-tech text-xs text-[#5C657A]">
                Signed in as <span className="text-white">{user.email}</span>
              </span>
            )}
          </p>
        </div>
        <button
          data-testid="account-signout-button"
          onClick={handleSignOut}
          className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 text-[#8A94A6] hover:text-white hover:border-[#00C3FF] hover:text-[#00C3FF] transition-colors font-mono-tech text-[10px] uppercase tracking-[0.22em]"
        >
          <LogOut size={12} />
          Sign Out
        </button>
      </div>

      <div className="mt-10">
        {err && (
          <div data-testid="orders-error" className="text-red-400 font-mono-tech text-sm">
            {String(err)}
          </div>
        )}

        {orders === null && !err && (
          <div className="text-[#8A94A6] font-mono-tech text-sm">Loading…</div>
        )}

        {orders && orders.length === 0 && (
          <div className="surface p-10 text-center">
            <Package2 className="mx-auto text-[#5C657A]" size={28} />
            <div className="overline mt-4">NO ORDERS YET</div>
            <h3 className="font-heading text-xl mt-2">Your catalog awaits.</h3>
            <Link to="/shop" className="btn-primary mt-6 inline-flex">
              Browse Compounds <ArrowRight size={14} />
            </Link>
          </div>
        )}

        {orders && orders.length > 0 && (
          <div className="space-y-px bg-white/5">
            {orders.map((o) => (
              <Link
                key={o.id}
                to={`/account/orders/${o.id}`}
                data-testid={`order-${o.id}`}
                className="bg-[#0C0C12] hover:bg-[#14141C] transition-colors p-6 flex items-center gap-6 group"
              >
                <div className="flex-1 min-w-0">
                  <div className="overline" style={{ fontSize: 10 }}>ORDER · {o.order_number || o.id.slice(0, 8).toUpperCase()}</div>
                  <div className="font-mono-tech text-xs text-[#5C657A] mt-1">
                    {new Date(o.created_at).toLocaleString()} · {(o.pricing?.line_items || o.items || []).length} item(s)
                  </div>
                </div>
                <div className="text-right hidden sm:block">
                  <div className="overline" style={{ fontSize: 10 }}>{((o.status || "PAID").replace(/_/g, " ")).toUpperCase()}</div>
                </div>
                <div className="font-mono-tech text-xl text-[#00C3FF] w-24 text-right">
                  ${Number(o.amount).toFixed(2)}
                </div>
                <ChevronRight size={16} className="text-[#5C657A] group-hover:text-[#00C3FF] transition-colors" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
